import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    FormGroupDirective,
    Validators,
    FormGroup, FormControl,
} from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, merge, Subscription } from 'rxjs';
import { map, shareReplay, take, tap } from 'rxjs/operators';
import { TwoFactorMethodEnum, UserEntity } from '../../dave-data-module/entities/user.entity';
import { State } from '../../dave-data-module/State';
import { DaveActions } from '../../dave-data-module/State/actions/actions';
import { BaseActionTypes } from '../../dave-data-module/State/actions/base.actions';
import { getToken } from '../../dave-data-module/State/selectors/base.selectors';
import { getUser } from '../../dave-data-module/State/selectors/users.selectors';
import { LoginService } from '../../services/login.service';
import { equalPasswordValidator, getErrorMessage, passwordValidator } from '../../helper/validation.helper';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
    @ViewChild('formRef')
    formRef: FormGroupDirective;

    @Input() Verification: {
        UserId: number;
        Code: string;
        Name: string;
    } = null;

    private subscriptions: Subscription[] = [];

    private user: UserEntity = null;
    public LoggedIn$ = this.store.select(getToken).pipe(
        map(token => !!(token && token.length > 0)),
    );

    public HidePassword = true;
    public NewPasswordDialog = false;
    public ResetPasswordDialog = false;
    public LoginFormGroup = new UntypedFormGroup({
        username: new UntypedFormControl('', Validators.required),
        password: new UntypedFormControl('', Validators.required),
    });
    public UsernameResetValidator = [Validators.required, Validators.email];
    public NewPasswordFormGroup: FormGroup<{ oldPassword: FormControl<string>; newPassword: FormControl<string>; confirmNewPassword: FormControl<string> }>;
    public ResetPasswordFormGroup = new UntypedFormGroup({
        usernameReset: new UntypedFormControl('', this.UsernameResetValidator),
    });
    public ActivateUserFormGroup = new FormGroup({
        newPassword: new FormControl<string>('', Validators.required),
        repeatNewPassword: new FormControl<string>('', [Validators.required, equalPasswordValidator(() => this.ActivateUserFormGroup?.controls?.newPassword)]),
    });

    public IsLoading$ = new BehaviorSubject(false);

    protected readonly TwoFactorMethodEnum = TwoFactorMethodEnum;
    protected mfaState$ = new BehaviorSubject<null | {
        type: TwoFactorMethodEnum;
        challengeId: string;
        loading: boolean;
    }>(null);
    protected mfaCode = new FormControl<string>('', [Validators.required, /* kann auch recoveryCode enthalten Validators.minLength(6), Validators.maxLength(6)*/]);
    protected mfaRememberDevice = false;
    constructor(
        private loginService: LoginService,
        private fb: UntypedFormBuilder,
        private store: Store<State>,
        private router: Router,
        private route: ActivatedRoute,
        private actions$: Actions<DaveActions>,
    ) {
        this.initForm();
    }

    ngOnInit(): void {
        this.subscriptions.push(
            combineLatest([this.store.select(getUser), this.store.select(getToken)]).subscribe(([user, token]) => {
                this.user = user;
            }),
            this.route.paramMap.subscribe((params: ParamMap) => {
                if (params.keys != null && params.keys.length === 3) {
                    this.Verification = {
                        Code: params.get('verificationCode'),
                        UserId: parseInt(params.get('userId'), 10),
                        Name: params.get('name'),
                    };
                }
                return params;
            }),
        );
    }

    ngOnDestroy(): void {
        this.clearMFATimer();
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    private initForm() {
        this.NewPasswordFormGroup = new FormGroup(
            {
                oldPassword: new FormControl<string>(null, [Validators.required]),
                newPassword: new FormControl<string>(null, [Validators.required, passwordValidator]),
                confirmNewPassword: new FormControl<string>(null, [Validators.required, equalPasswordValidator(() => this.NewPasswordFormGroup?.controls?.newPassword)]),
            },
        );
    }

    private mfaTimeout: ReturnType<typeof setTimeout> | null = null;

    private startMFATimer(): void {
        this.clearMFATimer();

        this.mfaTimeout = setTimeout(() => {
            this.cancelMFA();
        }, 10 * 60 * 1000); //10Minutes  expire of Challenge ID
    }

    private clearMFATimer(): void {
        if (this.mfaTimeout) {
            clearTimeout(this.mfaTimeout);
            this.mfaTimeout = null;
        }
    }

    public async LogIn() {
        this.IsLoading$.next(true);
        this.loginService.Login(this.UsernameForm.value, this.PasswordForm.value);

        const action = await firstValueFrom(this.actions$.pipe(ofType(BaseActionTypes.PerformLoginSuccess, BaseActionTypes.PerformLoginFailure)));
        this.IsLoading$.next(false);
        if (action.type === BaseActionTypes.PerformLoginFailure.type) {
            return;
        }
        if (action.Payload.token) {
            this.router.navigateByUrl('');
        } else {
            this.startMFATimer();
            // MFA required
            this.mfaState$.next({
                type: action.Payload.twoFactorMethod,
                challengeId: action.Payload.challengeId,
                loading: false,
            });
        }
    }
    protected cancelMFA() {
        this.clearMFATimer();
        this.mfaState$.next(null);
        this.mfaCode.reset();
        this.mfaRememberDevice = false;
    }

    protected async sendNewMFAMail() {
        this.store.dispatch(BaseActionTypes.SendNewMFAMail({ Payload: { challengeId: this.mfaState$.value.challengeId, } }));
        const actionPromise = firstValueFrom(
            this.actions$.pipe(
                ofType(
                    BaseActionTypes.SendNewMailSuccess,
                    BaseActionTypes.SendNewMailFailure,
                ),
            )
        );

        this.store.dispatch(BaseActionTypes.SendNewMFAMail({
            Payload: {
                challengeId: this.mfaState$.value.challengeId,
            },
        }));

        const action = await actionPromise;

        if (action.type === BaseActionTypes.SendNewMailFailure.type) {
            this.mfaState$.next({
                ...this.mfaState$.value,
                loading: false,
            });

            return;
        }

        this.mfaState$.next({
            ...this.mfaState$.value,
            challengeId: action.Payload.challengeId,
            loading: false,
        });

        this.startMFATimer();
    }

    public async submitMFA() {
        if (this.mfaCode.invalid) {
            return;
        }
        this.mfaState$.next({
            ...this.mfaState$.value,
            loading: true,
        });
        this.store.dispatch(BaseActionTypes.PerformMFARequest({ Payload: {code: this.mfaCode.value, challengeId: this.mfaState$.value.challengeId, trustThisDevice: this.mfaRememberDevice} }));
        const action = await firstValueFrom(this.actions$.pipe(ofType(BaseActionTypes.PerformMFASuccess, BaseActionTypes.PerformMFAFailure)));
        this.mfaState$.next({
            ...this.mfaState$.value,
            loading: false,
        });
        if (action.type === BaseActionTypes.PerformMFAFailure.type) {
            this.mfaCode.reset();
            return;
        }
        this.clearMFATimer();
        await this.router.navigateByUrl('');
    }

    public GetUserName() {
        return this.user?.DisplayName;
    }

    public Logout() {
        this.loginService.Logout();
        this.user = null;
    }

    public ToggleNewPasswordDialog(setting: boolean) {
        this.NewPasswordDialog = setting;
    }

    public ToggleResetDialog(setting: boolean) {
        this.ResetPasswordDialog = setting;
    }

    public ChangePassword() {
        this.store.dispatch(
            BaseActionTypes.ChangePassword({
                Payload: {
                    oldPassword: this.OldPasswordForm.value,
                    newPassword: this.NewPasswordForm.value,
                },
            }),
        );
    }

    public ResetPassword() {
        this.store.dispatch(BaseActionTypes.ResetPassword({ Payload: { Email: this.UsernameResetForm.value } }));
        this.ResetPasswordDialog = false;
    }

    public ActivateUser() {
        this.store.dispatch(
            BaseActionTypes.ActivateUser({
                Payload: {
                    verificationCode: this.Verification.Code,
                    userId: this.Verification.UserId,
                    password: this.ActivateUserNewPasswordForm.value,
                },
            }),
        );
        this.Verification = null;
    }

    public GetFormErrorMessage = getErrorMessage;

    get UsernameForm() {
        return this.LoginFormGroup.get('username');
    }
    get PasswordForm() {
        return this.LoginFormGroup.get('password');
    }

    get UsernameResetForm() {
        return this.ResetPasswordFormGroup.get('usernameReset');
    }

    get ActivateUserNewPasswordForm() {
        return this.ActivateUserFormGroup.get('newPassword');
    }
    get RepeatNewPasswordForm() {
        return this.ActivateUserFormGroup.get('repeatNewPassword');
    }

    get NewPasswordForm() {
        return this.NewPasswordFormGroup.get('newPassword');
    }
    get OldPasswordForm() {
        return this.NewPasswordFormGroup.get('oldPassword');
    }
    get ConfirmNewPasswordForm() {
        return this.NewPasswordFormGroup.get('confirmNewPassword');
    }
}
